<template>
    <Container>
        <div class="my-4 flex items-center justify-between gap-3">
            <div>
                <h1 class="text-2xl font-bold text-white">Create Battle</h1>
                <p class="mt-1 text-sm text-nomad-300">Choose two artists or songs, then give the battle a title and description.</p>
            </div>
            <Link href="/backstage/artist-battles"
                class="shrink-0 rounded-md bg-nomad-900 px-4 py-2 text-white transition-colors hover:bg-nomad-800">
                Cancel
            </Link>
        </div>

        <form @submit.prevent="submit" class="max-w-4xl space-y-4">
            <section class="rounded-xl bg-nomad-900 p-6">
                <h2 class="mb-4 text-lg font-semibold text-nomad-200">Battle details</h2>
                <div class="grid gap-4">
                    <label class="block text-sm font-medium text-nomad-300">
                        Title <span class="text-red-400">*</span>
                        <input v-model="form.title" type="text" maxlength="255" required
                            class="mt-2 w-full rounded-md border border-nomad-700 bg-nomad-800 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="e.g. Song of the Week" />
                        <span v-if="errors.title" class="mt-1 block text-sm text-red-400">{{ errors.title }}</span>
                    </label>

                    <label class="block text-sm font-medium text-nomad-300">
                        Description <span class="text-red-400">*</span>
                        <textarea v-model="form.description" rows="4" maxlength="10000" required
                            class="mt-2 w-full rounded-md border border-nomad-700 bg-nomad-800 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="Tell voters about this matchup." />
                        <span v-if="errors.description" class="mt-1 block text-sm text-red-400">{{ errors.description }}</span>
                    </label>
                </div>
            </section>

            <section class="rounded-xl bg-nomad-900 p-6">
                <h2 class="mb-1 text-lg font-semibold text-nomad-200">Competitors</h2>
                <p class="mb-4 text-sm text-nomad-300">Choose an artist or song for each side. Selected images appear in the matchup.</p>

                <div class="grid gap-4 md:grid-cols-2">
                    <div v-for="index in [0, 1]" :key="index" class="min-w-0 rounded-lg bg-nomad-800 p-4">
                        <h3 class="mb-3 text-sm font-semibold uppercase tracking-wide text-nomad-300">
                            Competitor {{ index + 1 }}
                        </h3>

                        <label class="block text-sm font-medium text-nomad-300">
                            Type
                            <select v-model="competitorTypes[index]" @change="changeCompetitorType(index)"
                                class="mt-2 w-full rounded-md border border-nomad-700 bg-nomad-900 text-white focus:border-nomad-accent focus:ring-nomad-accent">
                                <option value="artists">Artist</option>
                                <option value="songs">Song</option>
                            </select>
                        </label>

                        <div class="mt-3 flex min-h-20 items-center gap-3 rounded-lg bg-nomad-900 p-3">
                            <template v-if="selectedCompetitors[index]">
                                <img :src="selectedCompetitors[index].image || fallbackImage"
                                    :alt="selectedCompetitors[index].name"
                                    class="h-14 w-14 shrink-0 rounded-lg object-cover" />
                                <div class="min-w-0 flex-1">
                                    <p class="truncate font-semibold text-white">{{ selectedCompetitors[index].name }}</p>
                                    <p class="truncate text-sm text-nomad-300">
                                        {{ typeLabel(competitorTypes[index]) }}
                                        <template v-if="selectedCompetitors[index].artist?.name">
                                            · {{ selectedCompetitors[index].artist.name }}
                                        </template>
                                        <template v-else-if="selectedCompetitors[index].stage_name">
                                            · {{ selectedCompetitors[index].stage_name }}
                                        </template>
                                    </p>
                                </div>
                                <button type="button" @click="removeCompetitor(index)"
                                    class="rounded-md p-2 text-red-400 transition-colors hover:bg-red-500/10"
                                    :aria-label="`Remove ${selectedCompetitors[index].name}`">
                                    <span class="icon-x" aria-hidden="true"></span>
                                </button>
                            </template>
                            <p v-else class="text-sm text-nomad-400">
                                {{ typeLabel(competitorTypes[index]) }} {{ index + 1 }} not selected
                            </p>
                        </div>

                        <span v-if="errors[`competitor_${index === 0 ? 'one' : 'two'}_id`]"
                            class="mt-2 block text-sm text-red-400">
                            {{ errors[`competitor_${index === 0 ? 'one' : 'two'}_id`] }}
                        </span>

                        <label class="mt-4 block text-sm font-medium text-nomad-300">
                            Find a {{ competitorTypes[index] === 'artists' ? 'artist' : 'song' }}
                            <input v-model="searches[index]" type="search" autocomplete="off"
                                class="mt-2 w-full rounded-md border border-nomad-700 bg-nomad-900 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent"
                                :placeholder="competitorTypes[index] === 'artists' ? 'Search by artist or stage name' : 'Search by song title'" />
                        </label>
                        <p v-if="competitorErrors[index]" class="mt-2 text-sm text-red-400">
                            {{ competitorErrors[index] }}
                        </p>
                        <p v-else-if="loadingCompetitors[index]" class="mt-2 text-sm text-nomad-300">
                            Searching {{ competitorTypes[index] }}...
                        </p>
                        <ul v-else-if="searches[index].trim() && results[index].length"
                            class="mt-3 grid max-h-64 gap-2 overflow-y-auto">
                            <li v-for="competitor in results[index]" :key="`${competitor.type}-${competitor.id}`">
                                <button type="button" @click="selectCompetitor(index, competitor)"
                                    :disabled="isSelected(competitor)"
                                    class="flex w-full items-center gap-3 rounded-lg bg-nomad-900 p-3 text-left transition-colors hover:bg-nomad-700 disabled:cursor-not-allowed disabled:opacity-50">
                                    <img :src="competitor.image || fallbackImage" :alt="competitor.name"
                                        class="h-11 w-11 shrink-0 rounded-lg object-cover" />
                                    <span class="min-w-0 flex-1">
                                        <span class="block truncate font-medium text-white">{{ competitor.name }}</span>
                                        <span class="block truncate text-sm text-nomad-300">
                                            {{ typeLabel(competitor.type) }}
                                            <template v-if="competitor.artist?.name"> · {{ competitor.artist.name }}</template>
                                            <template v-else-if="competitor.stage_name"> · {{ competitor.stage_name }}</template>
                                        </span>
                                    </span>
                                    <span v-if="isSelected(competitor)" class="text-sm text-nomad-accent">Selected</span>
                                </button>
                            </li>
                        </ul>
                        <p v-else-if="searches[index].trim() && !loadingCompetitors[index]"
                            class="mt-3 text-sm text-nomad-400">
                            No {{ competitorTypes[index] }} found.
                        </p>
                    </div>
                </div>
            </section>

            <p v-if="submitError" class="rounded-lg bg-red-500/10 p-3 text-sm text-red-300">{{ submitError }}</p>
            <button type="submit" :disabled="processing || !selectedCompetitors[0] || !selectedCompetitors[1]"
                class="rounded-md bg-nomad-accent px-6 py-3 font-semibold text-nomad-900 transition-colors hover:bg-nomad-accent/80 disabled:cursor-not-allowed disabled:opacity-50">
                {{ processing ? 'Creating...' : 'Create draft battle' }}
            </button>
        </form>
    </Container>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import axios from 'axios';
import Container from '@/Pages/Backstage/Container.vue';

const fallbackImage = 'https://open.nomadlyrics.com/assets/images/no-artist.png';
const form = ref({ title: '', description: '', competitor_one_type: 'artists', competitor_one_id: null, competitor_two_type: 'artists', competitor_two_id: null });
const competitorTypes = ref(['artists', 'artists']);
const selectedCompetitors = ref([null, null]);
const searches = ref(['', '']);
const results = ref([[], []]);
const competitorErrors = ref(['', '']);
const loadingCompetitors = ref([false, false]);
const errors = ref({});
const submitError = ref('');
const processing = ref(false);
const searchTimeouts = [null, null];
const searchIds = [0, 0];

for (const index of [0, 1]) {
    watch(() => [competitorTypes.value[index], searches.value[index]], ([type, value]) => {
        clearTimeout(searchTimeouts[index]);
        const searchId = ++searchIds[index];
        competitorErrors.value[index] = '';

        if (!value.trim()) {
            results.value[index] = [];
            loadingCompetitors.value[index] = false;
            return;
        }

        loadingCompetitors.value[index] = true;
        searchTimeouts[index] = setTimeout(() => fetchCompetitors(index, type, value, searchId), 250);
    });
}

onBeforeUnmount(() => searchTimeouts.forEach(clearTimeout));

async function fetchCompetitors(index, type, value, searchId) {
    try {
        const response = await axios.get('/api/backstage/artist-battles/competitors', {
            params: { type, search: value },
        });
        if (searchId === searchIds[index]) results.value[index] = response.data.data || [];
    } catch (error) {
        if (searchId === searchIds[index]) {
            competitorErrors.value[index] = error.response?.data?.message || `Unable to load ${type}. Please try again.`;
        }
    } finally {
        if (searchId === searchIds[index]) loadingCompetitors.value[index] = false;
    }
}

function typeLabel(type) {
    return type === 'artists' ? 'Artist' : 'Song';
}

function changeCompetitorType(index) {
    const prefix = index === 0 ? 'competitor_one' : 'competitor_two';
    form.value[`${prefix}_type`] = competitorTypes.value[index];
    form.value[`${prefix}_id`] = null;
    selectedCompetitors.value[index] = null;
    searches.value[index] = '';
}

function isSelected(competitor) {
    return selectedCompetitors.value.some((selected) =>
        selected?.type === competitor.type && selected?.id === competitor.id
    );
}

function selectCompetitor(index, competitor) {
    if (isSelected(competitor)) return;
    const prefix = index === 0 ? 'competitor_one' : 'competitor_two';
    selectedCompetitors.value[index] = competitor;
    form.value[`${prefix}_type`] = competitor.type;
    form.value[`${prefix}_id`] = competitor.id;
    searches.value[index] = '';
    results.value[index] = [];
}

function removeCompetitor(index) {
    const prefix = index === 0 ? 'competitor_one' : 'competitor_two';
    selectedCompetitors.value[index] = null;
    form.value[`${prefix}_id`] = null;
    searches.value[index] = '';
}

async function submit() {
    processing.value = true;
    errors.value = {};
    submitError.value = '';
    try {
        await axios.post('/api/backstage/artist-battles', form.value);
        router.visit('/backstage/artist-battles');
    } catch (error) {
        const response = error.response;
        if (response?.status === 422 && response.data.errors) {
            errors.value = Object.fromEntries(
                Object.entries(response.data.errors).map(([field, messages]) => [field, messages[0]])
            );
            submitError.value = response.data.message || 'Please correct the highlighted fields.';
        } else {
            submitError.value = response?.data?.message || 'Unable to create the battle. Please try again.';
        }
    } finally {
        processing.value = false;
    }
}
</script>
